import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SHOPPING_CART_MODE } from "../../constants";
import { useSearchParamsOfBooking } from "../../hooks/searchParams/useSearchParamsOfBooking";
import { useAbleAdd2Cart } from "../../hooks/useAbleAdd2Cart";
import { useBookingGroupNo } from "../../hooks/useBookingGroupNo";
import { useBookingState } from "../../hooks/useBookingState";
import { useAddToCart } from "../../hooks/useCart";
import { usePopup } from "../../hooks/usePopup";
import { useResponsive } from "../../hooks/useResponsive";
import RefundModal from "../../pages/MyPage/components/MyBooking/RefundModal";
import { bookingActions } from "../../redux/slices/bookingSlice";
import BookingService from "../../services/bookingService";
import { splitBookingBasedOnAge } from "../../utils/bookingUtils";
import { hashStr } from "../../utils/utils";
import { useBookingCard } from "../booking-card/useBookingCard";
import { getChangeRequestData } from "./getChangeRequestData";
import Icon from "../image/Icon";
import { icCart } from "../image/image-src";

const AddToCartBtn: React.FC<{
  useBookingCardProps: ReturnType<typeof useBookingCard>;
}> = ({ useBookingCardProps }) => {
  const { isTabletLg } = useResponsive();
  const { priceDataHelper, priceDataHelperNext } = useBookingCardProps;
  const { bookingId, editBookingId, isEdit, isUpgrade } =
    useSearchParamsOfBooking();
  const genChangeRequestData = () =>
    getChangeRequestData({
      bookingId: editBookingId || bookingId,
      bookings: [useBookingCardProps.booking],
      isEdit,
      isUpgrade,
    });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const booking = useBookingState({ mode: "FORM" });
  const isUpdateBooking = booking.id === editBookingId;
  // const isPass = booking.product?.productType === "PASS";
  const isEditMode = booking.id === bookingId;
  const queryClient = useQueryClient();
  const refundPopup = usePopup({});
  const currentTotalAmount = priceDataHelper.totalOf("CART_TOTAL");
  // Amount already paid in previous order
  const previousPaidAmount =
    priceDataHelperNext.totalOfBeforeServiceFeeTotalOfOrder();
  // Difference between current and previous paid amount
  const adjustedAmount = currentTotalAmount - (previousPaidAmount ?? 0);
  const buttonCartRef = useRef<HTMLButtonElement>(null);

  const changeRequest = useMutation(
    () => BookingService.changeRequest(genChangeRequestData()),
    {
      onSuccess: (data) => {
        navigate("/mypage/booking", { replace: true });
      },
    },
  );

  const addToCart = useAddToCart({
    editBooking: isUpdateBooking,
    onSuccess: () => {
      if (isUpdateBooking) {
        const gotoCheckout = () =>
          navigate(
            `/shopping-cart?mode=${SHOPPING_CART_MODE.UPDATE_BOOKING}&bookIds=` +
              editBookingId +
              (isEdit ? `&isEdit=${isEdit}` : "") +
              (isUpgrade ? `&isUpgrade=${isUpgrade}` : ""),
            {
              replace: true,
            },
          );

        if (adjustedAmount === 0) {
          changeRequest.mutate();
          return;
        }
        if (adjustedAmount > 0) {
          gotoCheckout();
        }

        if (adjustedAmount < 0) {
          refundPopup.open();
        }
      }
    },
  });
  const { t } = useTranslation();

  const isPriceChanged = React.useMemo(() => {
    return (
      !isUpdateBooking ||
      toCompareBooking(booking) !== toCompareBooking(booking.prevBookingState)
    );
  }, [isUpdateBooking, booking]);
  // console.log("isPriceChanged1", toCompareBooking(booking));
  // console.log("isPriceChanged2", toCompareBooking(booking.prevBookingState));

  const ableAdd2Cart = useAbleAdd2Cart({
    totalPrice: currentTotalAmount,
    bookingAttributes: booking.bookingAttributes,
    mode: "FORM",
  });
  // const { reset: resetUiVersion } = useUiVersionReset();

  const [cartCount, setCartCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState<{
    open: boolean;
    from?: any;
    to?: any;
  }>({
    open: false,
  });
  const { bookGroupNo } = useBookingGroupNo({
    enabled:
      !isUpdateBooking &&
      !isEditMode &&
      booking.product?.productType === "PASS",
  });
  const handleAddToCart = () => {
    const buttonCartRect = buttonCartRef.current?.getBoundingClientRect();
    const cartRect = document
      .getElementById("cart-btn")
      ?.getBoundingClientRect();
    const cartMobileRect = document
      .getElementById("cart-mobile")
      ?.getBoundingClientRect();

    const itemCartEffect = {
      open: true,
      from: {
        x: buttonCartRect!.left + buttonCartRect!.width / 2,
        y: buttonCartRect!.top,
      },
      to: {
        x: isTabletLg ? cartRect!.left : cartMobileRect!.left,
        y: isTabletLg ? cartRect!.top : cartMobileRect!.top,
      },
    };

    try {
      // const pl = [];
      // const invalidAgeProductIds = extractInvalidAgeProductIds(booking);
      // const childrenLs = (booking.childrenLs ?? []).filter(
      //   (x) => !invalidAgeProductIds.includes(x.product?.id ?? 0),
      // );
      // pl.push(childrenLs);
      const pl = splitBookingBasedOnAge(booking);
      console.log("tend to add pl", pl);
      for (let i = 0; i < pl.length; i++) {
        const b = pl[i];
        const childrenLs = b.childrenLs ?? [];
        addToCart.mutate({
          bookGroupNo,
          booking: (function () {
            // if (invalidAgeProductIds.includes(booking.product?.id ?? 0)) {
            //   throw new Error("Invalid age");
            // }

            if (b.product?.productType === "PASS") {
              if (childrenLs.length === 0) {
                throw new Error("No valid children");
              }
              if (childrenLs.length === 1) {
                return childrenLs[0];
              }
            }
            return {
              ...b,
              childrenLs,
            };
          })(),
          overwrite: i === 0 && (isEditMode || isUpdateBooking),
          appendOnly: !pl.length || !!booking.childrenLs?.length,
        });
      }

      setCartCount(cartCount + pl.length);
      setIsAnimating(itemCartEffect);
      const shouldRefresh =
        booking.product?.productType === "PASS" &&
        !isUpdateBooking &&
        !isEditMode;
      if (isUpdateBooking && adjustedAmount < 0) {
      } else {
        setTimeout(() => {
          setIsAnimating({ open: false });
          queryClient.invalidateQueries(["booking"]);
          if (shouldRefresh) {
            // resetUiVersion();
            dispatch(bookingActions.resetBooking());
          }
        }, 800);
      }
    } catch (e) {
      console.error(e);
    }
  };

  console.log(isAnimating);

  return (
    <>
      {refundPopup.isOpen &&
        createPortal(
          <RefundModal
            ids={[]}
            bookingId={""}
            isOpen={refundPopup.isOpen}
            onClose={() => refundPopup.close()}
            changeRequest={genChangeRequestData()}
            onSuccess={() => {
              refundPopup.close();
              navigate("/mypage/booking");
            }}
          />,
          document.body,
        )}
      <div className="relative flex">
        <button
          ref={buttonCartRef}
          className={clsx(
            "cart--btn rounded-[3.4375rem] py-[0.8125rem] text-center text-base font-semibold leading-[1.25rem]",
            "ml-auto w-fit bg-primary px-3 text-[#232323] sm:ml-0 sm:w-full",
          )}
          onClick={handleAddToCart}
          disabled={!ableAdd2Cart || !isPriceChanged}
        >
          {t(isUpdateBooking || isEditMode ? "Change" : "Add to cart")}
        </button>{" "}
        {/* Floating Effect */}
        <AnimatePresence>
          {isAnimating.open && (
            <motion.div
              className="pointer-events-none fixed z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary/50"
              initial={{
                top: isAnimating.from.y,
                left: isAnimating.from.x,
                scale: 1,
              }}
              animate={{
                top: isAnimating.to.y,
                left: isAnimating.to.x,
                scale: 0.3,
                opacity: 0.5,
              }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <Icon src={icCart} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default AddToCartBtn;

const toCompareBooking = (b?: BookingState) => {
  const f = (ls?: BookingAttributeDTO[]) => {
    return (ls ?? [])
      .map((x) => ({
        productAttributeId: x.productAttribute?.id,
        value: {
          ...x.value,
          dpName: undefined,
          paType: undefined,
          paName: undefined,
          display: undefined,
        },
      }))
      .filter(
        (x) =>
          x?.value &&
          JSON.stringify(x.value) !== '{"quantity":0}' &&
          Object.values(x.value).filter((x) => x).length > 0,
      )
      .map((x) => {
        // return JSON.stringify(x);
        return hashStr(JSON.stringify(x));
      });
  };
  return JSON.stringify(
    [
      f(b?.bookingAttributes),
      ...(b?.childrenLs?.map((x) => f(x.bookingAttributes)) ?? []),
    ].sort(),
  );
};
